import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  HStack,
  Link,
  Text,
  useMediaQuery,
  VStack,
} from '@chakra-ui/react';
import { KeyRound } from 'lucide-react';
import { browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { useAuth } from '../hooks/useAuth';
import { ApiError } from '../lib/api';
import { ScanToSignIn } from '../components/auth/ScanToSignIn';
import { WelcomeShell } from '../components/page/WelcomeShell';
import { isAppleMobileTab } from '../lib/pwa';
import { t as translateNow, useT } from '../i18n';
import { fillParts } from '../components/page/fillParts';
import { StoreBadges } from '../components/site/StoreBadges';
import { useStoreLinks } from '../lib/siteConfig';

// Shared with the join pages' "I already use Twinden" (§1a).
export function signInErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'UNKNOWN_CREDENTIAL':
        return translateNow('account.login.unknownCredential');
      case 'CHALLENGE_NOT_FOUND':
        return translateNow('account.login.sessionExpired');
      case 'VERIFICATION_FAILED':
        return translateNow('account.login.verificationFailed');
      case 'CREDENTIAL_COMPROMISED':
        return translateNow('account.login.compromised');
      default:
        return err.message;
    }
  }
  if (err instanceof Error && err.name === 'NotAllowedError') {
    return translateNow('account.login.cancelled');
  }
  return translateNow('account.login.tryAgain');
}

export function LoginPage() {
  const navigate = useNavigate();
  const { loginWithPasskey, completeQrSignIn } = useAuth();
  // A phone is the scanner, not the scanned: there the code sits behind a
  // link (M49, D157).
  const [isPhone] = useMediaQuery('(max-width: 767px)', {
    ssr: false,
  });
  const [showScan, setShowScan] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // iPhone/iPad Safari: the Home Screen app is a separate device (D139).
  const [installFirst, setInstallFirst] = useState(isAppleMobileTab);
  const [loading, setLoading] = useState(false);
  const supported = browserSupportsWebAuthn();
  const t = useT();
  // On an iPhone, the App Store app before the Home Screen app (D346).
  const appStoreUrl = useStoreLinks().appStore;

  const handleLogin = async () => {
    setError(null);
    setLoading(true);
    try {
      await loginWithPasskey();
      navigate('/', { replace: true });
    } catch (err) {
      setError(signInErrorMessage(err));
      setLoading(false);
    }
  };

  const passkeyButton = (
    <>
      {error && (
        <Alert status="error" borderRadius="xl">
          <AlertIcon />
          {error}
        </Alert>
      )}
      {supported ? (
        <Button
          h="56px"
          fontSize="17px"
          leftIcon={<KeyRound size={20} />}
          onClick={handleLogin}
          isLoading={loading}
          loadingText={t('account.profile.waitingDevice')}
        >
          {t('account.login.signIn')}
        </Button>
      ) : (
        <Alert status="warning" borderRadius="xl">
          <AlertIcon />
          {t('account.login.unsupported')}
        </Alert>
      )}
      <Text fontSize="14px" color="muted" textAlign="center">
        {t('account.login.noPassword')}
      </Text>
    </>
  );
  const scanPanel = (
    <ScanToSignIn
      onApproved={(user, tokens) => {
        completeQrSignIn(user, tokens);
        navigate('/', { replace: true });
      }}
    />
  );

  return (
    <WelcomeShell
      title="Twinden"
      subtitle={t('account.login.subtitle')}
      footer={
        <>
          {fillParts(t('account.login.noAccount'), {
            request: (
              <Link
                as={RouterLink}
                to="/request-invite"
                color="accent"
                fontWeight={600}
              >
                {t('account.login.requestInvite')}
              </Link>
            ),
          })}{' '}
          <Link as={RouterLink} to="/" color="accent" fontWeight={600}>
            {t('account.login.whatIs')}
          </Link>{' '}
          {/* Can't sign in: the guides and questions are public (D290). */}
          <Link as={RouterLink} to="/help" color="accent" fontWeight={600}>
            {t('account.login.help')}
          </Link>
        </>
      }
    >
      <VStack spacing={4} align="stretch">
        {installFirst && (
          <Box bg="accent.tint" borderRadius="18px" px={4} py={3} role="note">
            <Text fontWeight={700} fontSize="16px">
              {t('account.login.installFirst')}
            </Text>
            <Text fontSize="15px" color="ink.body" lineHeight="21px" pt={1}>
              {appStoreUrl
                ? t('account.login.getAppBody')
                : fillParts(t('account.login.installFirstBody'), {
                    // The iPhone's own button names.
                    share: <strong>Share</strong>,
                    add: <strong>Add to Home Screen</strong>,
                  })}
            </Text>
            {appStoreUrl && (
              <Box pt={2}>
                <StoreBadges device="ios" />
              </Box>
            )}
            <HStack spacing={4} mt={2}>
              <Button
                as={RouterLink}
                to="/install"
                variant="link"
                size="sm"
                color="accent"
              >
                {t('account.login.showMe')}
              </Button>
              <Button
                variant="link"
                size="sm"
                color="muted"
                onClick={() => setInstallFirst(false)}
              >
                {t('account.login.continueSafari')}
              </Button>
            </HStack>
          </Box>
        )}
        {/* A computer leads with the code; a phone, the scanner, leads with
            its passkey and keeps the code behind a link (D157). */}
        {!isPhone && scanPanel}
        {passkeyButton}
        {isPhone &&
          (showScan ? (
            <>
              {scanPanel}
              <Button
                variant="link"
                size="sm"
                color="muted"
                onClick={() => setShowScan(false)}
              >
                {t('account.scan.hide')}
              </Button>
            </>
          ) : (
            <Button
              variant="link"
              size="sm"
              color="accent"
              onClick={() => setShowScan(true)}
            >
              {t('account.scan.onComputer')}
            </Button>
          ))}
      </VStack>
    </WelcomeShell>
  );
}
